import React, { useState, useEffect, useMemo } from "react";
import api from "../../core/api";
import "../factors/Factors.css";
import CustomerPicker from "../factors/CustomerPicker";
import JalaliDatePicker from "../../components/JalaliDatePicker";
import { Link } from "react-router-dom";
import { computeLedger, saveLedgerCache } from "./ledgerUtils";
import LedgerSkeleton from "./LedgerSkeleton";

const fa = (n) => Math.round(Number(n) || 0).toLocaleString("fa-IR");

// مانده: مثبت = بدهکار ، منفی = بستانکار
const balanceText = (b) => {
  if (!b) return "۰";
  return `${fa(Math.abs(b))} ${b > 0 ? "بد" : "بس"}`;
};

export default function Ledger() {
  const [customer, setCustomer] = useState(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [filter, setFilter] = useState("all");
  const [raw, setRaw] = useState(null); // پاسخ کامل سرور
  const [seller, setSeller] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  /* اطلاعات شرکت یک بار گرفته می‌شود (برای صفحه چاپ) */
  useEffect(() => {
    api
      .get("/company")
      .then((res) => setSeller(res.data?.company || {}))
      .catch(() => {});
  }, []);

  /* فقط با تغییر مشتری از سرور می‌گیریم؛ تاریخ و فیلتر سمت کلاینت اعمال می‌شود */
  useEffect(() => {
    if (!customer?.id) {
      setRaw(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await api.get(`/ledger/${customer.id}`);
        if (!cancelled) setRaw(res.data);
      } catch (err) {
        if (!cancelled)
          setError(err.response?.data?.error || "خطا در دریافت دفتر کل");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [customer]);

  const data = useMemo(
    () => (raw ? computeLedger(raw, { from, to, filter }) : null),
    [raw, from, to, filter]
  );

  const printQuery = new URLSearchParams({
    ...(from && { from }),
    ...(to && { to }),
    ...(filter !== "all" && { filter }),
  }).toString();

  /* قبل از باز شدن تب چاپ، داده را برای آن ذخیره می‌کنیم */
  const handlePrintClick = () => {
    if (!raw || !customer?.id) return;
    saveLedgerCache({ customerId: customer.id, raw, seller });
  };

  return (
    <>
      <div className="card">
        <h2>دفتر کل مشتری</h2>
        <CustomerPicker customer={customer} onSelect={setCustomer} />
        <div className="grid" style={{ marginTop: 12 }}>
          <div>
            <label>از تاریخ</label>
            <JalaliDatePicker
              value={from}
              onChange={setFrom}
              placeholder="از تاریخ"
              name="from"
            />
          </div>
          <div>
            <label>تا تاریخ</label>
            <JalaliDatePicker
              value={to}
              onChange={setTo}
              placeholder="تا تاریخ"
              name="to"
            />
          </div>
          <div className="field-full">
            <label>نمایش</label>
            <select value={filter} onChange={(e) => setFilter(e.target.value)}>
              <option value="all">فاکتور و تراکنش</option>
              <option value="factor">فاکتور</option>
              <option value="transaction">تراکنش</option>
            </select>
          </div>
        </div>
      </div>

      {error && <div className="card empty">{error}</div>}
      {loading && customer && <LedgerSkeleton />}


   {data && !loading && (
        <div className="card">
          <div className="card-header-row">
            <h2>{data.customer.name}</h2>
            <Link
              className="btn secondary small"
              target="_blank"
              rel="noopener noreferrer"
              onClick={handlePrintClick}
              to={`/dashboard/ledger/${customer.id}/print${
                printQuery ? `?${printQuery}` : ""
              }`}
            >
              <i className="fa-solid fa-print" /> چاپ
            </Link>
          </div>

          <div className="overflow">
            <table>
              <thead>
                <tr>
                  <th>تاریخ</th>
                  <th>نوع</th>
                  <th>شماره</th>
                  <th>شرح</th>
                  <th>بدهکار</th>
                  <th>بستانکار</th>
                  <th>مانده</th>
                </tr>
              </thead>
              <tbody>
                {(from || data.opening !== 0) && (
                  <tr>
                    <td colSpan="6" style={{ textAlign: "right" }}>
                      مانده از قبل
                    </td>
                    <td style={{ direction: "ltr" }}>
                      {balanceText(data.opening)}
                    </td>
                  </tr>
                )}
                {data.rows.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="empty">
                      گردشی ثبت نشده است.
                    </td>
                  </tr>
                ) : (
                  data.rows.map((r) => (
                    <tr key={`${r.kind}-${r.refId}`}>
                      <td>{r.date}</td>
                      <td>
                        {r.kind === "factor"
                          ? "فاکتور"
                          : `تراکنش - ${r.direction === "receive" ? "دریافت" : "پرداخت"}`}
                      </td>
                      <td>{r.ref}</td>
                      <td style={{ textAlign: "right" }}>{r.description}</td>
                      <td>{fa(r.debit)}</td>
                      <td>{fa(r.credit)}</td>
                      <td style={{ direction: "ltr", fontWeight: 700 }}>
                        {balanceText(r.balance)}
                      </td>
                    </tr>
                  ))
                )}
                <tr style={{ fontWeight: 800 }}>
                  <td colSpan="4">جمع</td>
                  <td>{fa(data.totalDebit)}</td>
                  <td>{fa(data.totalCredit)}</td>
                  <td style={{ direction: "ltr" }}>
                    {balanceText(data.closing)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}